/**
 * Company Search — react-query hooks over the api.ts data layer.
 *
 * Reads (lookup, profile, linked signals) are useQuery, gated with `enabled`
 * so nothing fires until the user has committed a query / selected a company.
 * The scan is a useMutation: it requests then reads status once (single
 * confirmation), so there is no polling query and no fabricated progress.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelCompanyScan,
  claimSignal,
  fetchCompanyBrief,
  fetchCompanyContacts,
  fetchCompanyPatternReads,
  fetchLinkedSignals,
  fetchLookupActivity,
  fetchScanStatusByOrg,
  generateCompanyBrief,
  getCompanyProfile,
  lookupCompany,
  requestCompanyScan,
  type ProfileResult,
} from "./api";
import {
  toCompanyBriefView,
  toCompanyContactViews,
  toCompanyPatternReadViews,
  toGeneratedBriefView,
  toLinkedSignalViews,
  toLookupView,
  toProfileView,
  toRecentLookupView,
  toScanLifecycleView,
  toTrendingLookupView,
} from "./logic";
import type {
  CompanyBriefView,
  CompanyContactView,
  CompanyPatternReadView,
  LinkedSignalView,
  LookupView,
  ProfileView,
  RawScanStatus,
  RecentLookupView,
  ScanLifecycleView,
  ScanTarget,
  TrendingLookupView,
} from "./types";

/** lookup_company — enabled only for a committed, non-empty query. */
export function useCompanyLookup(query: string) {
  const trimmed = query.trim();
  return useQuery<LookupView>({
    queryKey: ["company-search", "lookup", trimmed],
    queryFn: async () => toLookupView(await lookupCompany(trimmed)),
    enabled: trimmed.length > 0,
    staleTime: 60_000,
    retry: false,
  });
}

/** get_company_profile for a selected org. `not_found` maps to null so the
 *  page can render an honest absence state. displayName is threaded from the
 *  lookup selection (the profile contract does not carry a company name). */
export function useCompanyProfile(
  organizationId: string | null,
  displayName: string | null,
) {
  return useQuery<ProfileView | null>({
    queryKey: ["company-search", "profile", organizationId],
    queryFn: async () => {
      const result: ProfileResult = await getCompanyProfile(organizationId as string);
      if (result.status === "not_found") return null;
      return toProfileView(result.profile, displayName);
    },
    enabled: Boolean(organizationId),
    staleTime: 60_000,
    retry: false,
  });
}

/** get_company_contacts — a company's internal contacts (element d). */
export function useCompanyContacts(organizationId: string | null) {
  return useQuery<CompanyContactView[]>({
    queryKey: ["company-search", "contacts", organizationId],
    queryFn: async () => toCompanyContactViews(await fetchCompanyContacts(organizationId as string)),
    enabled: Boolean(organizationId),
    staleTime: 60_000,
    retry: false,
  });
}

/** get_signal_feed organization filter — a company's linked signals. */
export function useLinkedSignals(organizationId: string | null) {
  return useQuery<LinkedSignalView[]>({
    queryKey: ["company-search", "linked-signals", organizationId],
    queryFn: async () => toLinkedSignalViews(await fetchLinkedSignals(organizationId as string)),
    enabled: Boolean(organizationId),
    staleTime: 60_000,
    retry: false,
  });
}

/** request_company_scan (v2) — the unresolved-name in-session scan. The request
 *  returns the projection directly, so no separate confirmation read is needed. */
export function useRequestScan() {
  return useMutation<RawScanStatus, Error, ScanTarget>({
    mutationFn: (target: ScanTarget) => requestCompanyScan(target),
    retry: false,
  });
}

/** get_company_pattern_reads for a resolved org (element g). */
export function useCompanyPatternReads(organizationId: string | null) {
  return useQuery<CompanyPatternReadView[]>({
    queryKey: ["company-search", "pattern-reads", organizationId],
    queryFn: async () =>
      toCompanyPatternReadViews(await fetchCompanyPatternReads(organizationId as string)),
    enabled: Boolean(organizationId),
    staleTime: 60_000,
    retry: false,
  });
}

/** get_scan_status (organization-keyed) — the scan lifecycle for a resolved org.
 *  A null raw (no scan / 404) maps to the `requestable` phase, so the query data
 *  is always a lifecycle view. */
export function useScanStatus(organizationId: string | null) {
  return useQuery<ScanLifecycleView>({
    queryKey: ["company-search", "scan-status", organizationId],
    queryFn: async () => toScanLifecycleView(await fetchScanStatusByOrg(organizationId as string)),
    enabled: Boolean(organizationId),
    staleTime: 30_000,
    retry: false,
  });
}

/** request_company_scan for a resolved org; refreshes the org scan status. */
export function useRequestOrgScan(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation<RawScanStatus, Error, void>({
    mutationFn: () => requestCompanyScan({ organization_id: organizationId }),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["company-search", "scan-status", organizationId],
      });
    },
  });
}

/** cancel_company_scan by request_id; refreshes the org scan status so the
 *  request affordance returns. */
export function useCancelScan(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation<RawScanStatus, Error, string>({
    mutationFn: (requestId: string) => cancelCompanyScan(requestId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["company-search", "scan-status", organizationId],
      });
    },
  });
}

/** get_company_lookup_activity — first-visit rails (recents + trending). */
export function useLookupActivity() {
  return useQuery<{ recent: RecentLookupView[]; trending: TrendingLookupView[] }>({
    queryKey: ["company-search", "lookup-activity"],
    queryFn: async () => {
      const activity = await fetchLookupActivity();
      return {
        recent: (activity.recent_lookups ?? []).map(toRecentLookupView),
        trending: (activity.trending ?? []).map(toTrendingLookupView),
      };
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** claim_signal — inline claim from the company profile. On success the
 *  organization's linked-signals query is invalidated so the claimed row
 *  re-renders with its new (claimed) state; nothing is mutated client-side. */
export function useClaimCompanySignal(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (signalId: string) => claimSignal(signalId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["company-search", "linked-signals", organizationId],
      });
    },
  });
}

/* ------------------------------------------------------------------ *
 * CIB Layer 1 company brief
 * ------------------------------------------------------------------ */

/** Poll cadence for an in flight generation, inside the ratified 10 to 20s
 *  band. Generation runs about 3.5 minutes, so this is a low frequency check
 *  of recorded state, not a progress feed. */
export const BRIEF_POLL_MS = 15_000;

/** Company handle to brief_id, in memory only.
 *
 * Module scope so the handle survives the profile subtree unmounting when the
 * operator navigates away, and outlives the query cache's 5 minute gcTime,
 * which is shorter than a generation run. A reload clears it. Only the id is
 * held: never the research body, never bdse_context, and never localStorage or
 * sessionStorage. Losing it stays honest anyway, because asking again returns
 * either the same in flight generation (deduplicated) or the cached body. */
const briefHandles = new Map<string, string>();

export function briefHandleKey(
  organizationId: string | null,
  companyName: string | null,
): string | null {
  if (organizationId) return `org:${organizationId}`;
  const normalizedName = companyName?.trim().toLowerCase().replace(/\s+/g, " ") ?? "";
  return normalizedName ? `name:${normalizedName}` : null;
}

export function briefHandleFor(handleKey: string | null): string | null {
  return handleKey ? briefHandles.get(handleKey) ?? null : null;
}

/** get-company-brief by id. This is both the in flight poll and the single
 *  re-check that restores honest state on navigation away and return.
 *
 *  staleTime 0 with refetchOnMount "always" is the bdse_context discipline: the
 *  backend assembles that block live on every call, so a mounted surface never
 *  serves a remembered read. The interval runs only while the server says the
 *  generation is still running, and react-query pauses it while the tab is
 *  hidden, so nothing polls off screen. */
export function useCompanyBrief(briefId: string | null) {
  return useQuery<CompanyBriefView>({
    queryKey: ["company-search", "brief", briefId],
    queryFn: async () => toCompanyBriefView(await fetchCompanyBrief(briefId as string)),
    enabled: Boolean(briefId),
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: (query) =>
      query.state.data?.phase === "generating" ? BRIEF_POLL_MS : false,
    retry: false,
  });
}

/** generate-company-brief. One control serves both ratified outcomes: the
 *  cached body inside the 14 day TTL, or an acknowledgment that a background
 *  generation is running. The returned view seeds the read query so a cache hit
 *  renders without a second round trip, and the handle is remembered so a
 *  return visit can re-check it. */
export function useGenerateCompanyBrief(
  handleKey: string | null,
  organizationId: string | null,
) {
  const queryClient = useQueryClient();
  return useMutation<CompanyBriefView, Error, { companyName: string; regenerate: boolean }>({
    mutationFn: async (vars) =>
      toGeneratedBriefView(
        await generateCompanyBrief({
          companyName: vars.companyName,
          organizationId,
          regenerate: vars.regenerate,
        }),
      ),
    retry: false,
    onSuccess: (view) => {
      if (handleKey) briefHandles.set(handleKey, view.briefId);
      queryClient.setQueryData(["company-search", "brief", view.briefId], view);
    },
  });
}
